import { CompanyAdminLayout } from "../../components/CompanyAdmin/CompanyAdminLayout";
import { useCallback, useEffect, useRef, useState } from "react";
import TextInput from "../../components/UI/TextInput";
import CheckBox from "../../components/UI/CheckBox";
import ToggleableButton from "../../components/UI/ToggleagleButton";
import { ErrorMessageModal } from "../../components/UI/ErrorMessageModal";
import ConfirmMessageModal from "../../components/UI/ConfirmMessageModal";
import { Button } from "../../components/UI/Button";
import { companyUseCase } from "../../../di/container";
import { useParams } from "react-router-dom";
import Table from "../../components/UI/Table";
import Selection from "../../components/UI/Selection";
import { useAuthStore } from "../../stores/auth-store";

type Permission = "COMPANY_ADMIN" | "COMPANY_MANAGER" | "USER";

interface Member {
  isEnabled: boolean;
  name: string;
  email: string;
  mobile: string;
  permission: Permission;
  joinedDate: Date;
  leftDate?: Date | null;
  id: number;
  departmentId: number;
  departmentName: string;
}

// 권한 라벨
const PermissionLabel: React.FC<{
  permission: Permission;
}> = ({ permission }) => {
  return (
    <>
      {permission === "COMPANY_ADMIN" ? (
        <div className="text-center text-[#4285F4] bg-[#E8EFFF] rounded-[4px] py-[1px] px-[6px] font-medium size-max">
          회사 소유자
        </div>
      ) : null}

      {permission === "COMPANY_MANAGER" ? (
        <div className="text-center text-[#29B43F] bg-[#DFF8E4] rounded-[4px] py-[1px] px-[6px] font-medium size-max">
          회사 관리자
        </div>
      ) : null}

      {permission === "USER" ? (
        <div className="text-center text-[#555555] bg-[#F2F2F2] rounded-[4px] py-[1px] px-[6px] font-medium size-max">
          일반 사용자
        </div>
      ) : null}
    </>
  );
};

const CompanyMembers: React.FC = () => {
  const { companyId } = useParams();
  // 직원 권한 변경은 COMPANY_ADMIN(회사 소유자) 전용 — 매니저는 UI 미노출(백엔드도 403).
  const { isCompanyAdmin } = useAuthStore();
  const canChangeRole = isCompanyAdmin();
  // 탈퇴 처리도 권한 변경과 동일하게 COMPANY_ADMIN 전용(DELETE /api/company/{id}/user/{userId}).
  const canWithdrawMember = canChangeRole;

  const [errorMessageTitle, setErrorMessageTitle] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  // 탈퇴 처리 확인 모달 — 되돌릴 수 없는 작업이므로 대상 이메일을 본문에 명시한다.
  const [withdrawConfirmMessage, setWithdrawConfirmMessage] = useState("");
  const [withdrawTargets, setWithdrawTargets] = useState<Member[]>([]);
  const [isWithdrawing, setIsWithdrawing] = useState(false);

  const [isResetButtonEnabled, setIsResetButtonEnabled] = useState(false);
  const [searchKeyword, setSearchKeyword] = useState("");
  const [selectedPermission, setSelectedPermission] = useState("all");
  const [memberCount, setMemberCount] = useState(0);
  const [members, setMembers] = useState<Member[]>([]);

  useEffect(() => {
    (async () => {
      let totalCount = 0;
      let isCompanyListEmpty = false;
      let pageNumber = 0;

      while (!isCompanyListEmpty) {
        const users = await companyUseCase.getUserByCompanyId({
          companyId: Number(companyId),
          pageable: { page: pageNumber },
        });
        if (users.items.length < 1) {
          isCompanyListEmpty = true;
          continue;
        } else {
          totalCount += users.items.length;
        }
        pageNumber++;
      }

      setMemberCount(totalCount);
    })();
  }, [companyId]);

  // 선택된 멤버 관련 코드
  const [selectedMemberCount, setSelectedMemberCount] = useState(0);
  useEffect(() => {
    let tempCount = 0;
    for (let index = 0; index < members.length; index++) {
      if (members[index].isEnabled) {
        tempCount++;
      }
    }
    setSelectedMemberCount(tempCount);
  }, [members]);

  const isInitiallyLoaded = useRef(false);
  const [currentLoadingPage, setCurrentLoadedPage] = useState(0);
  const loadPage = useCallback(
    async (page: number) => {
      if (page === 0 && isInitiallyLoaded.current) return;
      isInitiallyLoaded.current = true;

      if (!companyId) {
        return;
      }

      const users = await companyUseCase.getUserByCompanyId({
        companyId: Number(companyId),
        pageable: { page: page },
      });
      const items = users.items;

      if (!items) {
        throw new Error("사용자 목록을 가져오는데 실패했습니다.");
      }

      const newList: Member[] = [];
      for (let index = 0; index < items.length; index++) {
        let currentUserPermission = items[index].role;
        newList.push({
          isEnabled: false,
          companyId: items[index].companyId,
          createdAt: items[index].createdAt,
          email: items[index].email,
          id: items[index].id,
          mobile: items[index].mobile,
          name: items[index].name,
          role: items[index].role,
          permission: currentUserPermission,
          joinedDate: new Date(items[index].createdAt),
          leftDate: null,
          departmentId: items[index].departmentId,
          departmentName: items[index].departmentName,
        } as Member);
      }

      setMembers((existingMembers) => {
        return [...existingMembers, ...newList];
      });
    },
    [companyId],
  );
  useEffect(() => {
    (async () => {
      await loadPage(currentLoadingPage);
    })();
  }, [companyId, currentLoadingPage, loadPage]);

  // 리셋 버튼 비활성화/활성화 여부 관리
  useEffect(() => {
    if (searchKeyword !== "") {
      setIsResetButtonEnabled(true);
    } else {
      setIsResetButtonEnabled(false);
    }
  }, [searchKeyword]);

  const loadMoreRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!loadMoreRef.current) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && members.length >= 20) {
          setCurrentLoadedPage((prevPage) => prevPage + 1);
        }
      },
      { threshold: 0.1 },
    );
    observer.observe(loadMoreRef.current);
    return () => observer.disconnect();
  }, [members.length]);

  // 최상단 전체 체크박스 상태 관리
  const [isTopCheckboxEnabled, setIsTopCheckboxEnabled] = useState(false);
  const toggleAllCheckboxes = () => {
    let isAllEnabled = true;
    for (let index = 0; index < members.length; index++) {
      if (!members[index].isEnabled) {
        isAllEnabled = false;
      }
    }

    if (isAllEnabled) {
      setMembers((prev) => {
        return prev.map((member) => {
          return { ...member, isEnabled: false };
        });
      });
      setIsTopCheckboxEnabled(false);
    } else {
      setMembers((prev) => {
        return prev.map((member) => {
          return { ...member, isEnabled: true };
        });
      });
      setIsTopCheckboxEnabled(true);
    }
  };

  const [roleChangeSelectedOption, setRoleChangeSelectedOption] = useState("_");
  useEffect(() => {
    switch (roleChangeSelectedOption) {
      case "COMPANY_MANAGER":
        updateSelectedMemberRole("COMPANY_MANAGER");
        break;
      case "USER":
        updateSelectedMemberRole("USER");
        break;
      default:
        break;
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roleChangeSelectedOption]);

  const [searchSelectedOption, setSearchSelectedOption] = useState("_");

  const loadCompanyMembers = async () => {
    setMembers([]);
    for (let i = 0; i <= currentLoadingPage; i++) {
      isInitiallyLoaded.current = false;
      await loadPage(i);
    }
  };

  const updateSelectedMemberRole = async (role: Permission) => {
    const enabledMembers = members.filter((member) => member.isEnabled);
    const isAllowed = enabledMembers.length > 0;
    if (!isAllowed) return;

    if (
      enabledMembers.filter((member) => member.permission === "COMPANY_ADMIN")
        .length > 0
    ) {
      setErrorMessageTitle("회사 소유자 권한은 변경할 수 없어요!");
      setErrorMessage(
        "회사 소유자는 권한의 양도, 변경이 불가능해요. 선택 해제 후 다시 시도해주세요.",
      );
      return;
    }

    if (
      role === "COMPANY_MANAGER" &&
      enabledMembers.filter(
        (member) => !member.departmentId || !member.departmentName,
      ).length > 0
    ) {
      setErrorMessageTitle("해당 유저가 어떤 부서에도 속해있지 않습니다.");
      setErrorMessage(
        enabledMembers
          .filter((member) => !member.departmentId || !member.departmentName)
          .map((member) => member.name)
          .join(", "),
      );
      return;
    }

    for (let index = 0; index < enabledMembers.length; index++) {
      switch (role) {
        case "COMPANY_ADMIN":
          await companyUseCase.updateUserRole({
            companyId: Number(companyId),
            targetUserIds: [enabledMembers[index].id],
            targetRequestRole: "COMPANY_ADMIN",
            departmentId: Number(enabledMembers[index].departmentId),
            newDepartmentName: enabledMembers[index].departmentName,
          });
          break;
        case "COMPANY_MANAGER":
          await companyUseCase.updateUserRole({
            companyId: Number(companyId),
            targetUserIds: [enabledMembers[index].id],
            targetRequestRole: "COMPANY_MANAGER",
            departmentId: Number(enabledMembers[index].departmentId),
            newDepartmentName: enabledMembers[index].departmentName,
          });
          break;
        case "USER":
          await companyUseCase.updateUserRole({
            companyId: Number(companyId),
            targetUserIds: [enabledMembers[index].id],
            targetRequestRole: "USER",
            departmentId: Number(enabledMembers[index].departmentId),
            newDepartmentName: enabledMembers[index].departmentName,
          });
          break;
      }
    }

    loadCompanyMembers();
  };

  /** 선택된 회원의 탈퇴 처리 확인 모달을 연다. */
  const confirmWithdrawSelectedMembers = () => {
    const enabledMembers = members.filter((member) => member.isEnabled);
    if (enabledMembers.length === 0) return;

    // 백엔드도 USER 만 허용한다(자기 자신 및 상위·동급 권한 계정은 403).
    const notGeneralMembers = enabledMembers.filter(
      (member) => member.permission !== "USER",
    );
    if (notGeneralMembers.length > 0) {
      setErrorMessageTitle("일반 사용자만 탈퇴 처리할 수 있어요!");
      setErrorMessage(
        `회사 소유자와 회사 관리자는 탈퇴 처리할 수 없어요. 선택 해제 후 다시 시도해주세요. (${notGeneralMembers
          .map((member) => member.name)
          .join(", ")})`,
      );
      return;
    }

    const targetEmails = enabledMembers
      .map((member) => member.email)
      .join(", ");

    setWithdrawTargets(enabledMembers);
    setWithdrawConfirmMessage(
      `${targetEmails} 계정을 탈퇴 처리할까요? 탈퇴 처리된 계정은 되돌릴 수 없으며, 해당 이메일로 탈퇴 안내 메일이 발송됩니다.`,
    );
  };

  /** 확인 모달에서 확정 시 실제 탈퇴 처리 */
  const withdrawSelectedMembers = async () => {
    const targets = withdrawTargets;
    setWithdrawConfirmMessage("");
    setWithdrawTargets([]);
    if (targets.length === 0 || !companyId) return;

    setIsWithdrawing(true);
    const failedEmails: string[] = [];
    let succeededCount = 0;

    for (let index = 0; index < targets.length; index++) {
      try {
        await companyUseCase.withdrawCompanyUser({
          companyId: Number(companyId),
          userId: targets[index].id,
        });
        succeededCount++;
      } catch {
        failedEmails.push(targets[index].email);
      }
    }
    setIsWithdrawing(false);

    if (failedEmails.length > 0) {
      setErrorMessageTitle("일부 회원을 탈퇴 처리하지 못했어요.");
      setErrorMessage(failedEmails.join(", "));
    }

    if (succeededCount > 0) {
      setMemberCount((prevCount) => Math.max(0, prevCount - succeededCount));
      setIsTopCheckboxEnabled(false);
      await loadCompanyMembers();
    }
  };

  return (
    <div className="relative">
      <ErrorMessageModal
        errorMessageTitle={errorMessageTitle}
        errorMessage={errorMessage}
        onClose={() => {
          setErrorMessageTitle("");
          setErrorMessage("");
        }}
      />
      {withdrawConfirmMessage && (
        <ConfirmMessageModal
          message={withdrawConfirmMessage}
          setMessage={setWithdrawConfirmMessage}
          submitFunction={withdrawSelectedMembers}
        />
      )}
      <CompanyAdminLayout>
        <div className="p-5">
          <div className="mb-[28px] / flex flex-col items-start gap-[0.5rem]">
            <h2 className="text-[1.375rem] / font-semibold">회원 관리</h2>
            <span className="text-[1rem] font-normal">
              총 {memberCount}명의 회원이 있습니다.
            </span>
          </div>
          <div className="text-[12px]">
            <div
              className="mb-[1.25rem] / p-[1.25rem]
          / border border-[#d9d9d9] / rounded-[0.625rem] / bg-white
          / flex flex-col md:flex-row gap-[0.75rem] / justify-stretch"
            >
              <TextInput
                value={searchKeyword}
                onChange={(e) => setSearchKeyword(e.target.value)}
                className="w-full"
                placeholder="회원 이름, 이메일, 전화번호로 검색"
              />

              <div className="md:w-[250px] h-[46px]">
                <Selection
                  selectedOption={searchSelectedOption}
                  setSelectedOption={setSearchSelectedOption}
                  options={[
                    { value: "_", name: "권한 선택" },
                    { value: "COMPANY_ADMIN", name: "회사 소유자" },
                    { value: "COMPANY_MANAGER", name: "회사 관리자" },
                    { value: "USER", name: "일반 사용자" },
                  ]}
                  img={
                    <>
                      <img
                        className="w-[1.125rem]"
                        alt="필터 아이콘"
                        src="/assets/icons/Filter 3.svg"
                      />
                    </>
                  }
                />
              </div>

              <ToggleableButton
                isEnabled={isResetButtonEnabled}
                className="min-w-[85px] min-h-[46px]"
                onClick={() => {
                  setSearchKeyword("");
                }}
              >
                초기화
              </ToggleableButton>
            </div>
            <div className="flex flex-col-reverse gap-[20px] md:flex-row">
              {selectedMemberCount === 0 || !canChangeRole ? null : (
                <div>
                  <div className="min-w-[204px] rounded-[0.5rem] p-[17px] border border-1 border-[#d9d9d9] bg-white">
                    <div className="text-start mb-[0.625rem]">
                      <h3 className="text-[16px] font-bold mb-[0.9375rem]">
                        {selectedMemberCount}명 선택됨
                      </h3>
                      <div className="text-[#aeaeae] font-bold">권한 변경</div>
                    </div>

                    <div>
                      <div className="relative / w-[6.5625rem] h-[2rem]">
                        <Selection
                          selectedOption={roleChangeSelectedOption}
                          setSelectedOption={setRoleChangeSelectedOption}
                          options={[
                            { value: "_", name: "권한 선택" },
                            { value: "COMPANY_MANAGER", name: "회사 관리자" },
                            { value: "USER", name: "일반 사용자" },
                          ]}
                        />
                      </div>
                    </div>

                    {/* 탈퇴 처리는 복구 불가 — 확인 모달을 거쳐야만 실행된다. */}
                    {canWithdrawMember && (
                      <div className="mt-[1.25rem] pt-[1.25rem] border-t border-[#eeeeee]">
                        <div className="text-[#aeaeae] font-bold mb-[0.625rem]">
                          회원 탈퇴
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          loading={isWithdrawing}
                          onClick={confirmWithdrawSelectedMembers}
                          className="w-full text-[#F24A4A] border-[#F24A4A] hover:bg-[#FEF2F2]"
                        >
                          탈퇴 처리
                        </Button>
                        <p className="mt-[0.5rem] text-[11px] text-[#888888] leading-[1.5]">
                          일반 사용자만 탈퇴 처리할 수 있으며, 처리 후
                          되돌릴 수 없습니다.
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              )}
              <Table
                columnWidths={[
                  "60px",
                  "120px",
                  "180px",
                  "150px",
                  "150px",
                  "120px",
                  "120px",
                ]}
                tabledata={{
                  head: [
                    <div className="w-[2.5938rem]">
                      <div
                        className="flex justify-center items-center"
                        onClick={toggleAllCheckboxes}
                      >
                        <CheckBox isEnabled={isTopCheckboxEnabled} />
                      </div>
                    </div>,
                    <>회원명</>,
                    <>이메일</>,
                    <>전화번호</>,
                    <>부서 이름</>,
                    <>권한</>,
                    <>가입일</>,
                  ],
                  body: members
                    .filter((item) => {
                      if (searchSelectedOption !== "_") {
                        switch (item.permission) {
                          case "COMPANY_ADMIN":
                            return searchSelectedOption === "COMPANY_ADMIN";
                          case "COMPANY_MANAGER":
                            return searchSelectedOption === "COMPANY_MANAGER";
                          case "USER":
                            return searchSelectedOption === "USER";
                        }
                      }

                      if (searchKeyword !== "") {
                        if (
                          !item?.name
                            ?.toLowerCase()
                            .includes(searchKeyword.toLowerCase()) &&
                          !item?.email
                            ?.toLowerCase()
                            .includes(searchKeyword.toLowerCase()) &&
                          !item?.mobile
                            ?.toLowerCase()
                            .includes(searchKeyword.toLowerCase())
                        ) {
                          return false;
                        }
                        return true;
                      }
                      return true;
                    })
                    .map((item, index) => {
                      return [
                        <div className="w-[2.5938rem]">
                          <div
                            role="button"
                            className="flex justify-center items-center"
                            onClick={() => {
                              setMembers((innerMemberInfoArray) =>
                                innerMemberInfoArray.map((member, i) =>
                                  i === index
                                    ? {
                                        ...member,
                                        isEnabled: !member.isEnabled,
                                      }
                                    : member,
                                ),
                              );
                            }}
                          >
                            <CheckBox isEnabled={item.isEnabled} />
                          </div>
                        </div>,
                        <>{item.name}</>,
                        <>{item.email}</>,
                        <>{item.mobile === "null" ? "-" : item.mobile}</>,
                        <>{item.departmentName ?? "-"}</>,
                        <>
                          {" "}
                          <PermissionLabel permission={item.permission} />
                        </>,
                        <>
                          {item.joinedDate
                            .toLocaleDateString("ko-KR", {
                              year: "numeric",
                              month: "2-digit",
                              day: "2-digit",
                            })
                            .replace(/-/g, ". ")}
                        </>,
                      ];
                    }),
                }}
              />
              <div ref={loadMoreRef} className="h-4" />
            </div>
          </div>
        </div>
      </CompanyAdminLayout>
    </div>
  );
};

export default CompanyMembers;
